import React, { useState, useEffect } from 'react';

import Box from '../../components/Box/Box';
import shouldShow from './loginNudgeDisplayLogic';
import Card from '../../components/Card/Card';
import Icon from '../../components/Icon/Icon';
import Title5 from '../../components/Title/Title5';
import { TextLarge, TextRegular } from '../../components/Text';
import { PrimaryButton } from '../../components/Button';

import { setLastDismissed, tracking } from './loginNudgeUtils';
import { domSelectors } from './loginNudgeConstants';

const WRAPPER_NAME = 'LoginNudgeWrapper';
const NAME = 'LoginNudge';

type TLoginNudge = {
  loginUrl?: string;
  onCloseClick?: Function;
  onCtaClick?: Function;
  translations: {
    greeting: string;
    welcome: string;
    loginText: string;
    list: {
      firstLine: string;
      secondLine: string;
      thirdLine: string;
    };
    loginButton: string;
  };
  loginButtonSelector?: string;
};

const LoginNudge = ({
  loginUrl = '/account/login.html',
  onCloseClick,
  onCtaClick,
  translations,
  loginButtonSelector,
}: TLoginNudge) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const [position, setPosition] = useState({ top: '', right: '' });

  const isVisible = !isDismissed && shouldShow() && position.top !== '' && position.right !== '';

  const removeNudge = () => {
    if (!isDismissed) {
      setLastDismissed();
      setIsDismissed(true);
    }
  };

  const handleCloseClick = () => {
    removeNudge();
    tracking.dismiss();
    if (onCloseClick) {
      onCloseClick();
    }
  };

  const handleFocusSearchbar = () => {
    removeNudge();
    tracking.focusIgnore();
  };

  const handleCtaClick = () => {
    removeNudge();
    tracking.cta();
    if (onCtaClick) {
      onCtaClick();
    }
  };

  const updatePosition = (loginButton) => {
    if (!loginButton) {
      setPosition({ top: '', right: '' });
      return;
    }

    const { top, right } = loginButton.getBoundingClientRect();

    setPosition({ top, right });
  };

  useEffect(() => {
    const searchBar = document.querySelector(domSelectors.SEARCH_INPUT);
    const loginButton = document.querySelector(loginButtonSelector || domSelectors.LOGIN_BUTTON);

    if (loginButton) {
      window.addEventListener('resize', function () {
        updatePosition(loginButton);
      });

      /** FIXME: This is a temporary work around to ensure the header has loaded */
      const timeUntilHeaderLoads = 10;
      setTimeout(() => {
        updatePosition(loginButton);
      }, timeUntilHeaderLoads);
    }

    if (searchBar) {
      searchBar.addEventListener('focus', handleFocusSearchbar);
    }

    return () => {
      if (searchBar) {
        searchBar.removeEventListener('focus', handleFocusSearchbar);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginButtonSelector]);

  if (!isVisible) return null;

  return (
    <Box as="div" name={WRAPPER_NAME} style={{ top: position.top, right: `calc(100% - ${position.right}px)` }}>
      <Box as="div" name={NAME}>
        <Box as="span" name="arrowPointer" parent={NAME} />
        <Card className={`${NAME}-customShadow`}>
          <Box as="div" name="body" parent={NAME}>
            <Box
              name="closeIcon"
              parent={NAME}
              as="span"
              onClick={handleCloseClick}
              aria-hidden="true"
              data-gdpr-implicit-consent-blacklist
            >
              <Icon name="close" />
            </Box>
            <Title5>
              {translations.greeting}
              <br />
              {translations.welcome}
            </Title5>
            <TextRegular>{translations.loginText}</TextRegular>
            <Box as="ul" name="list" parent={NAME}>
              <Box as="li" name="listItem" parent={NAME}>
                <Icon name="checkmark" />
                <TextLarge>{translations.list.firstLine}</TextLarge>
              </Box>
              <Box as="li" name="listItem" parent={NAME}>
                <Icon name="checkmark" />
                <TextLarge>{translations.list.secondLine}</TextLarge>
              </Box>
              <Box as="li" name="listItem" parent={NAME}>
                <Icon name="checkmark" />
                <TextLarge>{translations.list.thirdLine}</TextLarge>
              </Box>
            </Box>
          </Box>
          <Box as="div" name="ctaBlock" parent={NAME}>
            <PrimaryButton as="a" href={loginUrl} onClick={handleCtaClick}>
              {translations.loginButton}
            </PrimaryButton>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default LoginNudge;
